import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import {
  getUnitsWithProgress,
  getLessonsWithProgress,
  getStudentStats,
  getStudentLessonProgress,
  getUnitWorkshopsForCohorts,
  getStudentBadges,
  getStudentWorkshopAttendance,
  checkAndAwardBadges,
  syncStudentProgress
} from '@/lib/db'
import type {
  Badge,
  UnitWithProgress,
  LessonWithProgress,
  StudentStats,
  StudentLessonProgress,
  UnitWorkshop,
  StudentBadge,
  WorkshopAttendance
} from '@/types'
import {
  BookOpen,
  Video,
  Star,
  Flame,
  Award,
  Clock,
  ChevronRight,
  TrendingUp,
  CheckCircle,
  PlayCircle,
  Lock,
  Trophy,
  Loader2,
  Radio,
  Play,
  X
} from 'lucide-react'
import BadgeIcon from '@/components/badges/BadgeIcon'

export default function Dashboard() {
  const { t } = useTranslation()
  const { profile, student, isInCohort, cohortIds } = useAuth()
  const isPremium = student?.subscription_status === 'active'
  const hasFullAccess = isPremium && isInCohort
  const isPaidWaiting = isPremium && !isInCohort

  // Check if program hasn't started yet
  const programStartDate = student?.program_start_date
  const programNotStarted = (() => {
    if (!programStartDate) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const start = new Date(programStartDate + 'T12:00:00')
    start.setHours(0, 0, 0, 0)
    return start > today
  })()

  const [isLoading, setIsLoading] = useState(true)
  const [stats, setStats] = useState<StudentStats | null>(null)
  const [units, setUnits] = useState<UnitWithProgress[]>([])
  const [currentLessons, setCurrentLessons] = useState<LessonWithProgress[]>([])
  const [workshops, setWorkshops] = useState<UnitWorkshop[]>([])
  const [attendanceMap, setAttendanceMap] = useState<Record<string, WorkshopAttendance>>({})
  const [recentBadges, setRecentBadges] = useState<StudentBadge[]>([])
  const [allLessonProgress, setAllLessonProgress] = useState<StudentLessonProgress[]>([])
  const [newBadges, setNewBadges] = useState<Badge[]>([])

  useEffect(() => {
    if (profile && student) {
      loadDashboardData()
    }
  }, [profile, student])

  const loadDashboardData = async () => {
    if (!profile || !student) return
    setIsLoading(true)

    try {
      // Sync progress data first (fixes any stale lesson/unit progress)
      await syncStudentProgress(student.id)

      // Fetch all data in parallel (usar student.id para tablas de progreso)
      const [statsRes, unitsRes, lessonProgressRes, workshopsRes, badgesRes, attendanceRes] = await Promise.all([
        getStudentStats(student.id),
        getUnitsWithProgress(student.id),
        getStudentLessonProgress(student.id),
        cohortIds.length > 0 ? getUnitWorkshopsForCohorts(cohortIds) : Promise.resolve({ data: [], error: null }),
        getStudentBadges(student.id),
        getStudentWorkshopAttendance(student.id)
      ])

      if (statsRes.error) console.error('Dashboard: stats error', statsRes.error)
      if (unitsRes.error) console.error('Dashboard: units error', unitsRes.error)
      if (lessonProgressRes.error) console.error('Dashboard: lesson progress error', lessonProgressRes.error)
      if (workshopsRes.error) console.error('Dashboard: workshops error', workshopsRes.error)
      if (badgesRes.error) console.error('Dashboard: badges error', badgesRes.error)
      if (attendanceRes.error) console.error('Dashboard: attendance error', attendanceRes.error)

      if (statsRes.data) setStats(statsRes.data)
      if (badgesRes.data) setRecentBadges(badgesRes.data.slice(0, 3))

      if (lessonProgressRes.data) setAllLessonProgress(lessonProgressRes.data)

      const unitsData = unitsRes.data || []
      setUnits(unitsData)

      // Store all workshops
      const workshopsData = workshopsRes.data || []
      console.log('Dashboard: workshops loaded', workshopsData.length)
      setWorkshops(workshopsData)

      // Build attendance lookup by workshop_id
      if (attendanceRes.data) {
        const map: Record<string, WorkshopAttendance> = {}
        for (const a of attendanceRes.data) {
          map[a.workshop_id] = a
        }
        setAttendanceMap(map)
      }

      // Find the current unit (in_progress or first available)
      const currentUnit = unitsData.find(u =>
        u.progress?.status === 'in_progress'
      ) || unitsData.find(u =>
        u.progress?.status === 'available'
      )

      // Load lessons for the current unit (data already synced by syncStudentProgress above)
      if (currentUnit) {
        const lessonsRes = await getLessonsWithProgress(student.id, currentUnit.id)
        if (lessonsRes.data) {
          console.log('[DASH] Lessons after sync:', lessonsRes.data.map(l => ({
            title: l.title,
            status: l.progress?.status,
            completed: l.progress?.contents_completed,
            total: l.progress?.total_contents
          })))
          setCurrentLessons(lessonsRes.data)
        }
      }

      // Auto-award badges based on current progress
      checkAndAwardBadges(student!.id).then(awarded => {
        if (awarded.length > 0) {
          setNewBadges(awarded)
        }
      }).catch(err => console.error('Badge auto-award error:', err))
    } catch (err) {
      console.error('Error loading dashboard:', err)
    } finally {
      setIsLoading(false)
    }
  }

  // Helper: get workshop title
  const getWorkshopTitle = (w: UnitWorkshop) => {
    return w.title_es || w.title || ''
  }

  // Helper: get unit status for a workshop's unit
  const getUnitStatusForWorkshop = (w: UnitWorkshop) => {
    const unit = units.find(u => u.id === w.unit_id)
    return unit?.progress?.status || 'locked'
  }

  // Calculate real progress based on contents completed (more granular than lessons)
  const totalUnits = units.length || 4
  const contentsCompleted = allLessonProgress.reduce((acc, lp) => acc + (lp.contents_completed || 0), 0)
  const totalContents = totalUnits * 4 * 4 // 4 units × 4 lessons × 4 contents
  const lessonsCompleted = allLessonProgress.filter(lp => lp.status === 'completed').length
  const progressPercentage = totalContents > 0
    ? Math.round((contentsCompleted / totalContents) * 100)
    : 0

  // Current unit info
  const currentUnit = units.find(u =>
    u.progress?.status === 'in_progress'
  ) || units.find(u =>
    u.progress?.status === 'available'
  )

  // Get lessons that are actionable (in_progress or available)
  const actionableLessons = currentLessons.filter(l =>
    l.progress?.status === 'in_progress' ||
    l.progress?.status === 'available' ||
    l.progress?.status === 'completed'
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header de bienvenida */}
      <div className="bg-gradient-to-r from-primary to-primary/80 rounded-2xl p-6 text-primary-foreground">
        <h1 className="text-2xl font-bold mb-2">
          Hola, {profile?.first_name}! 👋
        </h1>
        <p className="opacity-90">
          {currentUnit
            ? `Estás en la unidad: ${currentUnit.title}`
            : '¡Comienza tu aprendizaje del francés!'}
        </p>

        {/* Barra de progreso general */}
        <div className="mt-4">
          <div className="flex justify-between text-sm mb-2">
            <span>
              Progreso A1
              {' — '}
              {contentsCompleted}/{totalContents} contenidos
            </span>
            <span>{progressPercentage}%</span>
          </div>
          <div className="h-3 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Notificación de badges nuevos */}
      {newBadges.length > 0 && (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-4 animate-in fade-in slide-in-from-top duration-500">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center shrink-0">
              <Trophy className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-yellow-800">
                Nuevas insignias obtenidas!
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {newBadges.map(badge => (
                  <Link
                    key={badge.id}
                    to="/badges"
                    className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-yellow-200 rounded-full text-sm font-medium text-yellow-700 hover:bg-yellow-50"
                  >
                    <Award className="w-3.5 h-3.5" />
                    {badge.name_es || badge.name}
                  </Link>
                ))}
              </div>
            </div>
            <button
              onClick={() => setNewBadges([])}
              className="text-yellow-400 hover:text-yellow-600 shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Banner: programa aún no comienza */}
      {programNotStarted && programStartDate && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 text-lg mb-1">
                Tu programa comienza pronto
              </h3>
              <p className="text-blue-800">
                Recuerda que tu programa parte el{' '}
                <strong>
                  {new Date(programStartDate + 'T12:00:00').toLocaleDateString('es-CL', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </strong>
                . Ese día se desbloqueará el resto del contenido y tendrás tu primera sesión de <strong>bienvenida</strong> con tu profesor.
              </p>
              <p className="text-sm text-blue-600 mt-2">
                Mientras tanto, puedes explorar la Unidad 1.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats rápidos */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl p-4 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Star className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{student?.total_points || 0}</p>
              <p className="text-xs text-muted-foreground">{t('gamification.points')}</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl p-4 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Flame className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{student?.streak_days || 0}</p>
              <p className="text-xs text-muted-foreground">{t('gamification.streakDays')}</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl p-4 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{lessonsCompleted}</p>
              <p className="text-xs text-muted-foreground">
                Lecciones completadas
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl p-4 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Video className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats?.workshopsAttended || 0}</p>
              <p className="text-xs text-muted-foreground">{t('workshops.title')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Units progress overview */}
      {units.length > 0 && (
        <div className="bg-card rounded-xl border overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-semibold flex items-center gap-2">
              <Trophy className="w-5 h-5 text-primary" />
              Progreso por unidad
            </h2>
            <Link to="/units" className="text-sm text-primary hover:underline">
              Ver todo
            </Link>
          </div>
          <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {units.map((unit) => {
              const status = unit.progress?.status || 'locked'
              const unitLessons = unit.progress?.lessons_completed || 0
              const unitTotal = unit.progress?.total_lessons || 4
              const unitPct = unitTotal > 0 ? Math.round((unitLessons / unitTotal) * 100) : 0
              const isContentLocked = !hasFullAccess && unit.order_num > 1

              if (isContentLocked) {
                return (
                  <div
                    key={unit.id}
                    className="rounded-lg p-3 border border-dashed border-gray-300 bg-muted/20 opacity-75"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      {isPaidWaiting ? (
                        <Clock className="w-4 h-4 text-blue-400" />
                      ) : (
                        <Lock className="w-4 h-4 text-gray-400" />
                      )}
                      <span className="text-xs font-medium text-muted-foreground">
                        Unidad {unit.order_num}
                      </span>
                    </div>
                    <p className="text-sm font-medium truncate mb-1">{unit.title}</p>
                    {isPaidWaiting ? (
                      <p className="text-xs text-blue-600 font-medium">
                        Próximamente
                      </p>
                    ) : (
                      <>
                        <p className="text-xs text-orange-600 font-medium">
                          No disponible
                        </p>
                        <Link
                          to="/planes"
                          className="text-xs text-primary hover:underline mt-1 inline-block"
                        >
                          Versión completa →
                        </Link>
                      </>
                    )}
                  </div>
                )
              }

              return (
                <Link
                  key={unit.id}
                  to={status !== 'locked' ? `/unit/${unit.id}` : '#'}
                  className={`rounded-lg p-3 border transition-colors ${
                    status === 'locked'
                      ? 'bg-muted/30 opacity-60 cursor-not-allowed'
                      : status === 'completed'
                        ? 'bg-green-50 border-green-200 hover:bg-green-100'
                        : status === 'in_progress'
                          ? 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                          : 'bg-card hover:bg-muted/50'
                  }`}
                  onClick={status === 'locked' ? (e) => e.preventDefault() : undefined}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {status === 'completed' ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : status === 'in_progress' ? (
                      <PlayCircle className="w-4 h-4 text-blue-600" />
                    ) : status === 'locked' ? (
                      <Lock className="w-4 h-4 text-gray-400" />
                    ) : (
                      <PlayCircle className="w-4 h-4 text-gray-500" />
                    )}
                    <span className="text-xs font-medium text-muted-foreground">
                      Unidad {unit.order_num}
                    </span>
                  </div>
                  <p className="text-sm font-medium truncate mb-2">{unit.title}</p>
                  {status !== 'locked' && (
                    <div>
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            status === 'completed' ? 'bg-green-500' : 'bg-blue-500'
                          }`}
                          style={{ width: `${unitPct}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {unitLessons}/{unitTotal} lecciones
                      </p>
                    </div>
                  )}
                </Link>
              )
            })}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Continuar aprendiendo - lecciones de la unidad actual */}
        <div className="bg-card rounded-xl border overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-semibold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Continuar
              {currentUnit && (
                <span className="text-xs font-normal text-muted-foreground ml-1">
                  — {currentUnit.title}
                </span>
              )}
            </h2>
            {currentUnit && (
              <Link to={`/unit/${currentUnit.id}`} className="text-sm text-primary hover:underline">
                Ver todo
              </Link>
            )}
          </div>
          <div className="divide-y">
            {actionableLessons.length > 0 ? (
              actionableLessons.map((lesson) => {
                const status = lesson.progress?.status || 'locked'
                const contentsCompleted = lesson.progress?.contents_completed || 0
                const contentsTotal = lesson.progress?.total_contents || 4
                const lessonPct = contentsTotal > 0 ? Math.round((contentsCompleted / contentsTotal) * 100) : 0
                return (
                  <Link
                    key={lesson.id}
                    to={status !== 'locked' ? `/lesson/${lesson.id}` : '#'}
                    className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors"
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      status === 'completed'
                        ? 'bg-green-100'
                        : status === 'in_progress'
                          ? 'bg-blue-100'
                          : 'bg-primary/10'
                    }`}>
                      {status === 'completed' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <BookOpen className={`w-5 h-5 ${
                          status === 'in_progress' ? 'text-blue-600' : 'text-primary'
                        }`} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{lesson.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {status === 'completed'
                          ? 'Completada'
                          : status === 'in_progress'
                            ? `${contentsCompleted}/${contentsTotal} contenidos`
                            : 'Disponible'}
                      </p>
                    </div>
                    <div className="text-right">
                      {status === 'completed' ? (
                        <Award className="w-5 h-5 text-green-500" />
                      ) : status === 'in_progress' ? (
                        <span className="text-sm font-medium text-primary">{lessonPct}%</span>
                      ) : (
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                  </Link>
                )
              })
            ) : (
              <div className="p-6 text-center text-muted-foreground">
                <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  ¡Comienza tu primera unidad!
                </p>
                <Link
                  to="/units"
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90"
                >
                  Ver unidades
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Talleres por unidad */}
        <div className="bg-card rounded-xl border overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-semibold flex items-center gap-2">
              <Video className="w-5 h-5 text-primary" />
              Talleres
            </h2>
          </div>
          {!hasFullAccess ? (
            <div className="p-8 text-center">
              {isPaidWaiting ? (
                <>
                  <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-7 h-7 text-blue-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">
                    Estamos preparando tu plataforma
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    En breve tendrás acceso completo.
                  </p>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-7 h-7 text-orange-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">
                    Disponible en versión completa
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Accede a talleres en vivo con un profesor nativo
                  </p>
                  <Link
                    to="/planes"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors"
                  >
                    Desbloquear
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </>
              )}
            </div>
          ) : (
          <div className="divide-y">
            {workshops.length > 0 ? (
              workshops.map((w) => {
                const unitStatus = getUnitStatusForWorkshop(w)
                const isLocked = unitStatus === 'locked'
                const attendance = attendanceMap[w.id]
                const attended = attendance?.status === 'attended'
                const wsStatus = (w as UnitWorkshop & { status?: string }).status || 'scheduled'
                const hasRecording = !!(w as UnitWorkshop & { recording_url?: string }).recording_url
                const isLive = wsStatus === 'live'
                const isCompleted = wsStatus === 'completed'
                const isScheduled = wsStatus === 'scheduled'
                const now = new Date()
                const scheduledDate = w.scheduled_date ? new Date(w.scheduled_date) : null
                const isUpcoming = isScheduled && scheduledDate && scheduledDate > now
                const unitInfo = w.unit || units.find(u => u.id === w.unit_id)

                return (
                  <div
                    key={w.id}
                    className={`p-4 ${isLocked ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                        isLocked
                          ? 'bg-gray-100'
                          : isLive
                            ? 'bg-red-100'
                            : hasRecording || isCompleted
                              ? 'bg-green-100'
                              : 'bg-purple-100'
                      }`}>
                        {isLocked ? (
                          <Lock className="w-5 h-5 text-gray-400" />
                        ) : isLive ? (
                          <Radio className="w-5 h-5 text-red-600" />
                        ) : hasRecording || isCompleted ? (
                          <Play className="w-5 h-5 text-green-600" />
                        ) : (
                          <Video className="w-5 h-5 text-purple-600" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{getWorkshopTitle(w)}</p>
                        <p className="text-xs text-muted-foreground">
                          {unitInfo
                            ? `Unidad ${unitInfo.order_num}: ${unitInfo.title}`
                            : ''}
                        </p>

                        {/* Status & date */}
                        {!isLocked && (
                          <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                            {isLive && (
                              <span className="inline-flex items-center gap-1 text-red-600 font-medium">
                                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                                En vivo
                              </span>
                            )}
                            {isUpcoming && scheduledDate && (
                              <span className="inline-flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />
                                {scheduledDate.toLocaleDateString(
                                  'es-ES',
                                  { weekday: 'short', day: 'numeric', month: 'short' }
                                )}
                                {' — '}
                                {scheduledDate.toLocaleTimeString(
                                  'es-ES',
                                  { hour: '2-digit', minute: '2-digit' }
                                )}
                              </span>
                            )}
                            {hasRecording && (
                              <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                                <CheckCircle className="w-3.5 h-3.5" />
                                Grabación disponible
                              </span>
                            )}
                            {attended && (
                              <span className="inline-flex items-center gap-1 text-green-600">
                                <Award className="w-3.5 h-3.5" />
                                Asistido
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Action */}
                      <div className="shrink-0">
                        {isLocked ? (
                          <Lock className="w-4 h-4 text-gray-300" />
                        ) : isLive ? (
                          <a
                            href={w.meeting_url || w.jitsi_room_name ? `#jitsi-${w.jitsi_room_name}` : '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700"
                          >
                            Unirse
                          </a>
                        ) : hasRecording ? (
                          <a
                            href={(w as UnitWorkshop & { recording_url?: string }).recording_url || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700"
                          >
                            Ver
                          </a>
                        ) : isUpcoming ? (
                          <span className="text-xs text-muted-foreground">
                            <Clock className="w-4 h-4" />
                          </span>
                        ) : (
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="p-6 text-center text-muted-foreground">
                <Video className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  No hay talleres disponibles por ahora
                </p>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Badges recientes */}
      {recentBadges.length > 0 && (
        <div className="bg-card rounded-xl border overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-semibold flex items-center gap-2">
              <Award className="w-5 h-5 text-primary" />
              Badges recientes
            </h2>
            <Link to="/badges" className="text-sm text-primary hover:underline">
              Ver todos
            </Link>
          </div>
          <div className="p-4 flex gap-4 overflow-x-auto">
            {recentBadges.map((sb) => (
              <div
                key={sb.id}
                className="flex flex-col items-center gap-2 min-w-[80px]"
              >
                <BadgeIcon
                  iconUrl={sb.badge?.icon_url}
                  category={sb.badge?.category || 'progress'}
                  rarity={sb.badge?.rarity}
                  requirementType={sb.badge?.requirement_type || ''}
                  requirementValue={sb.badge?.requirement_value || 0}
                  earned={true}
                  size={48}
                />
                <span className="text-xs text-center font-medium">
                  {sb.badge?.name_es || sb.badge?.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Banner upgrade (si no es premium) o espera (si pagó pero sin cohorte) */}
      {isPaidWaiting && (
        <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl p-6 text-white">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-lg mb-1">
                Estamos preparando tu plataforma
              </h3>
              <p className="opacity-90">
                En breve tendrás acceso completo.
              </p>
            </div>
            <div className="px-6 py-3 bg-white/20 rounded-lg font-semibold text-center">
              <Clock className="w-5 h-5 inline-block mr-2" />
              Próximamente
            </div>
          </div>
        </div>
      )}
      {!isPremium && (
        <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl p-6 text-white">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-lg mb-1">
                Período de prueba
              </h3>
              <p className="opacity-90">
                Desbloquea todo el contenido con acceso completo
              </p>
            </div>
            <Link
              to="/planes"
              className="px-6 py-3 bg-white text-orange-600 rounded-lg font-semibold hover:bg-white/90 transition-colors text-center"
            >
              {t('subscription.upgrade')}
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
